import React, { useState, useEffect, useCallback } from 'react';
import { FiX, FiSearch, FiPlus, FiSave, FiUser, FiArrowLeft, FiEdit2 } from 'react-icons/fi';
import { listarTomadores, salvarTomador } from '../../../services/nfTomadores';
import { consultarCnpj } from '../../../services/nf';
import SeletorMunicipio from '../../../componentes/SeletorMunicipio';
import { formatarDocumento, mascararDocumento, mascararCep, soDigitos } from '../../../utils/documento';
import { buscarCep } from '../../../services/cep';
import styles from './Emitir.module.css';
import useEsc from '../../../hooks/useEsc';
import CampoMoeda from '../../../componentes/CampoMoeda';

/* Seleção de tomador fora da tela de emissão: lá ficam só CNPJ e nome,
   em leitura. Todo o cadastro (consulta na Receita, endereço, gravar)
   acontece aqui dentro, pra emissão não virar um formulário gigante. */

const FORM_VAZIO = {
  documento: '', nome: '', email: '', telefone: '', cep: '',
  codigoMunicipio: '', logradouro: '', numero: '', complemento: '', bairro: '',
  valorServico: '', servicos: [], descricaoServico: '', vencimentoTipo: '', vencimentoValor: '', adicionais: [],
};
/* valor final = unitário × quantidade; sem quantidade, o unitário é o valor
   do serviço. É o valor final que vai pra emissão. */
function valorFinal(unitario, quantidade) {
  if (unitario === '' || unitario == null) return '';
  const u = Number(unitario);
  const q = Number(quantidade);
  if (!(q > 0)) return String(u);
  return String(Math.round(u * q * 100) / 100);
}

const SERVICO_VAZIO = { descricao: '', valorUnitario: '', quantidade: '', valor: '' };

const ADICIONAIS_VAZIOS = [{ rotulo: '', valor: '' }, { rotulo: '', valor: '' }, { rotulo: '', valor: '' }];

/* modoCadastro: aberto pelo ícone Fornecedores do cliente -- clicar edita (não
   escolhe pra nota) e gravar volta pra lista */
function ModalTomadores({ clienteId, onFechar, onSelecionar, modoCadastro = false, titulo }) {
  useEsc(onFechar);
  const [lista, setLista] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [busca, setBusca] = useState('');
  const [erro, setErro] = useState('');
  const [modo, setModo] = useState('lista'); // 'lista' | 'form'
  const [form, setForm] = useState(FORM_VAZIO);
  const [salvando, setSalvando] = useState(false);
  const [consultando, setConsultando] = useState(false);
  const [msg, setMsg] = useState('');

  const carregar = useCallback(() => {
    setCarregando(true);
    return listarTomadores(clienteId)
      .then(setLista)
      .catch(e => setErro(e.message))
      .finally(() => setCarregando(false));
  }, [clienteId]);
  useEffect(() => { carregar(); }, [carregar]);

  const set = (c, v) => setForm(f => ({ ...f, [c]: v }));

  const q = busca.trim().toLowerCase();
  const digitos = busca.replace(/\D/g, '');
  const visiveis = q
    ? lista.filter(t =>
        t.nome.toLowerCase().includes(q) || (digitos && t.documento.includes(digitos)))
    : lista;

  /* endereço pelo CEP: completa rua, bairro e município; número e
     complemento ficam com quem digita */
  async function preencherPorCep(cep, { soVazios = false } = {}) {
    const d = soDigitos(cep);
    if (d.length !== 8) return false;
    try {
      const e = await buscarCep(d);
      setForm(f => {
        const v = (campo, novo) => (soVazios && f[campo] ? f[campo] : (novo || f[campo]));
        return {
          ...f,
          logradouro: v('logradouro', e.logradouro),
          bairro: v('bairro', e.bairro),
          codigoMunicipio: v('codigoMunicipio', e.codigoMunicipio),
          complemento: f.complemento || e.complemento || '',
        };
      });
      return true;
    } catch {
      return false;
    }
  }

  function digitarCep(valor) {
    const d = soDigitos(valor).slice(0, 8);
    const antes = soDigitos(form.cep);
    set('cep', d);
    if (d.length === 8 && d !== antes) {
      preencherPorCep(d).then(ok => setMsg(ok ? 'Endereço preenchido pelo CEP.' : 'CEP não encontrado -- preencha o endereço à mão.'));
    }
  }

  function digitarDocumento(valor) {
    const d = soDigitos(valor).slice(0, 14);
    const antes = soDigitos(form.documento);
    set('documento', d);
    /* CNPJ completo: já busca na Receita, sem precisar clicar na lupa */
    if (d.length === 14 && d !== antes) consultar(d);
  }

  async function consultar(docInformado) {
    const doc = soDigitos(typeof docInformado === 'string' ? docInformado : form.documento);
    if (doc.length !== 14) { setMsg('A consulta na Receita só vale para CNPJ (14 dígitos).'); return; }
    setConsultando(true); setMsg('');
    try {
      const r = await consultarCnpj(doc);
      setForm(f => ({
        ...f,
        /* a razão social é o nome que vai na nota; o fantasia só serve de
           reserva pra quem não tem razão social preenchida na Receita */
        nome: r.razaoSocial || r.nomeFantasia || f.nome,
        email: r.email || f.email,
        telefone: r.telefone || f.telefone,
        cep: r.cep || f.cep,
        codigoMunicipio: r.codigoMunicipio || f.codigoMunicipio,
        logradouro: r.logradouro || f.logradouro,
        numero: r.numero || f.numero,
        complemento: r.complemento || f.complemento,
        bairro: r.bairro || f.bairro,
      }));
      /* a Receita costuma vir sem rua (MEI, principalmente): os Correios completam */
      if (r.cep && !r.logradouro) await preencherPorCep(r.cep, { soVazios: true });
      setMsg(`Encontrado na Receita${r.situacaoCadastral ? ` (${r.situacaoCadastral})` : ''}.`);
    } catch (e) {
      setMsg(`${e.message} -- digite o CEP que o endereço vem sozinho.`);
    } finally {
      setConsultando(false);
    }
  }

  async function gravar() {
    const doc = form.documento.replace(/\D/g, '');
    if (!doc) { setMsg('Informe o CPF/CNPJ.'); return; }
    if (!form.nome.trim()) { setMsg('Informe o nome / razão social.'); return; }
    setSalvando(true); setMsg('');
    try {
      const servicos = (form.servicos || [])
        .map(s => ({ ...s, valor: valorFinal(s.valorUnitario, s.quantidade) }))
        .filter(s => String(s.descricao || '').trim() || s.valor !== '');
      await salvarTomador(clienteId, { ...form, servicos, documento: doc, nome: form.nome.trim(), comCobranca: true });
      if (modoCadastro) {
        setSalvando(false); setModo('lista'); setMsg('');
        await carregar();
        return;
      }
      /* grava e já usa: era o que a pessoa queria ao cadastrar na hora da
         emissão -- obrigar a achar na lista depois seria um passo a troco
         de nada */
      onSelecionar({ ...form, documento: doc, nome: form.nome.trim() });
    } catch (e) {
      setMsg(e.message);
      setSalvando(false);
    }
  }

  return (
    <div className={styles.modalFundo}>
      <div className={styles.modalCaixa} onClick={e => e.stopPropagation()}>
        <div className={styles.modalTopo}>
          <h3 className={styles.modalTitulo}>
            {modo === 'form' ? (form.id ? (modoCadastro ? 'Editar fornecedor' : 'Editar tomador') : (modoCadastro ? 'Cadastrar fornecedor' : 'Cadastrar tomador'))
              : (titulo || 'Selecionar tomador')}
          </h3>
          <button type="button" className={styles.btnIcone} onClick={onFechar}><FiX size={16} /></button>
        </div>

        {modo === 'lista' ? (
          <>
            <div className={styles.modalBarra}>
              <div className={styles.campoComBotao} style={{ flex: 1 }}>
                <FiSearch size={13} className={styles.buscaIcone} />
                <input className={styles.entrada} style={{ paddingLeft: 28 }}
                  placeholder="Buscar por CNPJ/CPF ou nome..."
                  value={busca} onChange={e => setBusca(e.target.value)} autoFocus />
              </div>
              <button type="button" className={styles.btnAvancar}
                onClick={() => { setForm({ ...FORM_VAZIO, documento: digitos }); setMsg(''); setModo('form'); }}>
                <FiPlus size={13} /> Cadastrar
              </button>
            </div>

            {erro && <p className={styles.erroCaixa}>{erro}</p>}

            <div className={styles.modalLista}>
              {carregando ? (
                <p className={styles.modalVazio}>Carregando...</p>
              ) : visiveis.length === 0 ? (
                <p className={styles.modalVazio}>
                  {lista.length === 0
                    ? (modoCadastro ? 'Nenhum fornecedor cadastrado para este cliente ainda. Eles também entram sozinhos a cada nota emitida.' : 'Nenhum tomador cadastrado para este cliente ainda.')
                    : 'Nenhum tomador encontrado com esse termo.'}
                </p>
              ) : (
                visiveis.map(t => (
                  <div key={t.id || t.documento} style={{ display: 'flex', alignItems: 'stretch', gap: 4 }}>
                    <button type="button" style={{ flex: 1 }}
                      className={styles.tomadorItem}
                      onClick={() => (modoCadastro ? (setForm({ ...FORM_VAZIO, ...t }), setMsg(''), setModo('form')) : onSelecionar(t))}>
                      <span className={styles.tomadorIcone}><FiUser size={13} /></span>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span className={styles.tomadorNome}>{t.nome}</span>
                        <span className={styles.tomadorDoc}>{formatarDocumento(t.documento)}{t.servicos?.length > 1 ? ` · ${t.servicos.length} serviços` : (t.valorServico !== '' && t.valorServico != null ? ` · R$ ${Number(t.valorServico).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '')}</span>
                      </span>
                    </button>
                    <button type="button" className={styles.btnIcone} title="Editar tomador (cadastro e cobrança padrão)"
                      onClick={() => { setForm({ ...FORM_VAZIO, ...t }); setMsg(''); setModo('form'); }}><FiEdit2 size={13} /></button>
                  </div>
                ))
              )}
            </div>
          </>
        ) : (
          <>
            <div className={styles.modalCorpo}>
              <div className={styles.grade}>
                <div className={styles.campo}>
                  <span className={styles.rotulo}>CPF/CNPJ *</span>
                  <div className={styles.campoComBotao}>
                    <input className={styles.entrada} value={mascararDocumento(form.documento)} inputMode="numeric"
                      onChange={e => digitarDocumento(e.target.value)} placeholder="CPF ou CNPJ" />
                    <button type="button" className={styles.btnIcone} title="Consultar na Receita"
                      onClick={() => consultar()} disabled={consultando}>
                      <FiSearch size={14} />
                    </button>
                  </div>
                </div>
                <div className={styles.campo}>
                  <span className={styles.rotulo}>Nome / Razão social *</span>
                  <input className={styles.entrada} value={form.nome}
                    onChange={e => set('nome', e.target.value)} />
                </div>
              </div>

              {msg && <p className={styles.dica}>{consultando ? 'Consultando...' : msg}</p>}

              <div className={styles.gradeCompacta}>
                <div className={styles.campo}>
                  <span className={styles.rotulo}>Telefone</span>
                  <input className={styles.entrada} value={form.telefone}
                    onChange={e => set('telefone', e.target.value)} />
                </div>
                <div className={styles.campo}>
                  <span className={styles.rotulo}>E-mail</span>
                  <input className={styles.entrada} value={form.email}
                    onChange={e => set('email', e.target.value)} />
                </div>
                <div className={styles.campo}>
                  <span className={styles.rotulo}>CEP</span>
                  <input className={styles.entrada} value={mascararCep(form.cep)} inputMode="numeric"
                    placeholder="00000-000" onChange={e => digitarCep(e.target.value)} />
                </div>
                <div className={styles.campo}>
                  <span className={styles.rotulo}>Município</span>
                  <SeletorMunicipio className={styles.entrada} valor={form.codigoMunicipio}
                    onChange={v => set('codigoMunicipio', v)} />
                </div>
                <div className={`${styles.campo} ${styles.campoLargo}`}>
                  <span className={styles.rotulo}>Logradouro</span>
                  <input className={styles.entrada} value={form.logradouro}
                    onChange={e => set('logradouro', e.target.value)} />
                </div>
                <div className={styles.campo}>
                  <span className={styles.rotulo}>Número</span>
                  <input className={styles.entrada} value={form.numero}
                    onChange={e => set('numero', e.target.value)} />
                </div>
                <div className={styles.campo}>
                  <span className={styles.rotulo}>Complemento</span>
                  <input className={styles.entrada} value={form.complemento}
                    onChange={e => set('complemento', e.target.value)} />
                </div>
                <div className={styles.campo}>
                  <span className={styles.rotulo}>Bairro</span>
                  <input className={styles.entrada} value={form.bairro}
                    onChange={e => set('bairro', e.target.value)} />
                </div>
              </div>

              {/* cobrança padrão deste tomador: tudo opcional, vem preenchido na
                  emissão e pode ser alterado nela */}
              <p className={styles.rotulo} style={{ marginTop: 14, fontWeight: 800 }}>Cobrança padrão (opcional)</p>
              <div className={styles.gradeCompacta}>
                <div className={styles.campo}>
                  <span className={styles.rotulo}>Vencimento</span>
                  <select className={styles.entrada} value={form.vencimentoTipo || ''} onChange={e => set('vencimentoTipo', e.target.value)}>
                    <option value="">Não informar</option>
                    <option value="dia_fixo_mes">Dia fixo no mês da nota</option>
                    <option value="dia_fixo">Dia fixo no mês seguinte</option>
                    <option value="informar">Informar a data completa na emissão</option>
                  </select>
                </div>
                {form.vencimentoTipo && form.vencimentoTipo !== 'informar' && (
                  <div className={styles.campo}>
                    <span className={styles.rotulo}>{form.vencimentoTipo.startsWith('dia_fixo') ? 'Dia do mês (fim de semana antecipa)' : 'Quantos dias'}</span>
                    <input className={styles.entrada} type="number" min="1" max={form.vencimentoTipo.startsWith('dia_fixo') ? 31 : undefined} value={form.vencimentoValor ?? ''}
                      onChange={e => set('vencimentoValor', e.target.value)} />
                  </div>
                )}
              </div>

              {/* serviços padrão: mais de um = na emissão a tela pergunta qual
                  vai na nota */}
              <p className={styles.rotulo} style={{ marginTop: 10 }}>Serviços e valores (com mais de um, a emissão pergunta qual vai na nota; quantidade se informa na emissão)</p>
              {(form.servicos?.length ? form.servicos : [SERVICO_VAZIO]).map((sv, i, lista) => {
                const mudar = (campo, v) => set('servicos', lista.map((x, j) => (j === i ? { ...x, [campo]: v } : x)));
                return (
                  <div key={i} style={{ display: 'flex', gap: 6, marginTop: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <input className={styles.entrada} style={{ flex: '2 1 220px' }} placeholder={`Descrição do serviço ${i + 1}`}
                      value={sv.descricao || ''} onChange={e => mudar('descricao', e.target.value)} />
                    {/* quantidade é da emissão (muda a cada nota), não do cadastro;
                        aqui só se marca se este serviço é cobrado por quantidade */}
                    <CampoMoeda className={styles.entrada} style={{ flex: '1 1 120px' }} placeholder={sv.informarQuantidade ? 'Valor unitário (R$)' : 'Valor (R$)'}
                      value={sv.valorUnitario ?? ''} onChange={v => setForm(f => ({ ...f, servicos: lista.map((x, j) => (j === i ? { ...x, valorUnitario: v, quantidade: '' } : x)) }))} />
                    <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#4A6B8A', whiteSpace: 'nowrap' }}
                      title="Na emissão aparecem valor unitário e quantidade; o valor do serviço é a multiplicação">
                      <input type="checkbox" checked={!!sv.informarQuantidade} onChange={e => mudar('informarQuantidade', e.target.checked)} />
                      Informar quantidade na emissão
                    </label>
                    {lista.length > 1 && (
                      <button type="button" className={styles.btnIcone} title="Tirar este serviço"
                        onClick={() => set('servicos', lista.filter((_, j) => j !== i))}>×</button>
                    )}
                  </div>
                );
              })}
              {(form.servicos?.length || 1) < 10 && (
                <button type="button" className={styles.btnSecundario} style={{ marginTop: 6 }}
                  onClick={() => set('servicos', [...(form.servicos?.length ? form.servicos : [SERVICO_VAZIO]), SERVICO_VAZIO])}>+ Serviço</button>
              )}
              <p className={styles.rotulo} style={{ marginTop: 10 }}>Valores adicionais (aparecem à parte no corpo da nota)</p>
              {[0, 1, 2].map(i => {
                const lista = (form.adicionais?.length ? [...form.adicionais, ...ADICIONAIS_VAZIOS] : ADICIONAIS_VAZIOS).slice(0, 3);
                const a = lista[i];
                const mudar = (campo, v) => set('adicionais', lista.map((x, j) => (j === i ? { ...x, [campo]: v } : x)));
                return (
                  <div key={i} style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                    <input className={styles.entrada} placeholder={`Adicional ${i + 1} (ex.: Material)`} value={a.rotulo || ''} onChange={e => mudar('rotulo', e.target.value)} />
                    <CampoMoeda className={styles.entrada} style={{ maxWidth: 140 }} placeholder="R$" value={a.valor ?? ''} onChange={v => mudar('valor', v)} />
                  </div>
                );
              })}
            </div>

            <div className={styles.modalRodape}>
              <button type="button" className={styles.btnSecundario}
                onClick={() => { setModo('lista'); setMsg(''); }}>
                <FiArrowLeft size={13} /> Voltar à lista
              </button>
              <button type="button" className={styles.btnAvancar} onClick={gravar} disabled={salvando}>
                <FiSave size={13} /> {salvando ? 'Gravando...' : (modoCadastro ? 'Gravar' : 'Gravar e usar')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ModalTomadores;
